import { useEffect, useState, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
const triggerKeyDownHandler = (event, setIsOpen) => {
    switch (event.code) {
        case 'Space':
        case 'Enter':
            event.preventDefault();
            setIsOpen(true);
            break;
        case 'Escape':
            setIsOpen(false);
            break;
        default:
            break;
    }
};
const popoverKeyDownHandler = (event, setIsOpen) => {
    if (event.code === 'Escape') {
        setIsOpen(false);
    }
};
/**
 * Handles returning correct accessibility attributes and keyboard handlers for a popover
 * Also manages open close state of popover in controlled or uncontrolled mode
 **/
export const usePopover = ({ isOpen: isOpenProp, setIsOpen: setIsOpenProp, focusableElementRef, id, ...args } = {}) => {
    const previousIsOpen = useRef(undefined);
    const [isOpenState, setIsOpen] = useState(false);
    const [referenceElement, setReferenceElement] = useState(null);
    const popoverId = useRef(id ?? uuidv4());
    const isOpen = isOpenProp ?? isOpenState;
    useEffect(() => {
        if (focusableElementRef && isOpen) {
            focusableElementRef.current?.focus({
                preventScroll: true,
            });
        }
    }, [focusableElementRef, isOpen]);
    useEffect(() => {
        /**
         * If `previousIsOpen` is defined and does not match `isOpen`,
         * and if the popover was just closed, then focus the trigger element
         */
        if (previousIsOpen.current !== isOpen) {
            if (!isOpen && referenceElement) {
                referenceElement?.focus();
            }
        }
        previousIsOpen.current = isOpen;
    }, [isOpen, previousIsOpen, referenceElement]);
    const handleSetIsOpen = (isOpen) => {
        setIsOpen(isOpen);
        if (setIsOpenProp) {
            setIsOpenProp(isOpen);
        }
    };
    const { triggerProps, ...popoverArgs } = args;
    return {
        popoverTriggerProps: {
            ref: setReferenceElement,
            onClick: (e) => {
                if (triggerProps?.onClick) {
                    triggerProps.onClick(e);
                }
                handleSetIsOpen(!isOpen);
            },
            onKeyDown: (e) => {
                if (triggerProps?.onKeyDown) {
                    triggerProps.onKeyDown(e);
                }
                triggerKeyDownHandler(e, handleSetIsOpen);
            },
            'aria-expanded': isOpen,
            'aria-haspopup': true,
            'aria-controls': `popover-${popoverId.current}`,
        },
        popoverProps: {
            ...popoverArgs,
            referenceElement,
            isOpen: isOpen,
            onClose: (isOpen) => handleSetIsOpen(isOpen),
            onKeyDown: (e) => {
                if (args.onKeyDown) {
                    args.onKeyDown(e);
                }
                popoverKeyDownHandler(e, handleSetIsOpen);
            },
            id: `popover-${popoverId.current}`,
        },
    };
};
